<template>
  <div class="row c-container-item-options">
    <!-- Width Control -->
    <div class="mb-3">
      <cvt-select
        mode="dropdown"
        :value="widthType"
        :label="'Width'"
        :background-mode="mode"
        @input="(value) => updateWidthType(value)"
      >
        <option value="default" v-text="'Default'" />
        <option value="full-width" v-text="'Full Width (100%)'" />
        <option value="inline-auto" v-text="'Inline Auto'" />
        <option value="custom" v-text="'Custom'" />
      </cvt-select>
    </div>

    <!-- Custom Width Input (shown when custom is selected) -->
    <div v-if="widthType === 'custom'" class="mb-3">
      <div class="row">
        <div class="col-8">
          <cvt-input
            type="number"
            :value="customWidthValue"
            :label="'Custom Width'"
            :placeholder="'Enter width value'"
            @input="(value) => updateCustomWidthValue(value)"
          />
        </div>
        <div class="col-4">
          <cvt-select
            mode="dropdown"
            :value="customWidthUnit"
            :label="'Unit'"
            :background-mode="mode"
            @input="(value) => updateCustomWidthUnit(value)"
          >
            <option value="px" v-text="'px'" />
            <option value="%" v-text="'%'" />
            <option value="rem" v-text="'rem'" />
            <option value="em" v-text="'em'" />
          </cvt-select>
        </div>
      </div>
    </div>

    <!-- Height Control -->
    <div v-if="canConfigureHeight" class="mb-3">
      <cvt-select
        mode="dropdown"
        :value="heightType"
        :label="'Height'"
        :background-mode="mode"
        @input="(value) => updateHeightType(value)"
      >
        <option value="auto" v-text="'Auto'" />
        <option value="full-height" v-text="'Full Height (100%)'" />
        <option value="custom" v-text="'Custom'" />
      </cvt-select>
    </div>

    <!-- Custom Width Input (shown when custom is selected) -->
    <div v-if="canConfigureHeight && heightType === 'custom'" class="mb-3">
      <div class="row">
        <div class="col-8">
          <cvt-input
            type="number"
            :value="customHeightValue"
            :label="'Custom Height'"
            :placeholder="'Enter height value'"
            @input="(value) => updateCustomHeightValue(value)"
          />
        </div>
        <div class="col-4">
          <cvt-select
            mode="dropdown"
            :value="customHeightUnit"
            :label="'Unit'"
            :background-mode="mode"
            @input="(value) => updateCustomHeightUnit(value)"
          >
            <option value="px" v-text="'px'" />
            <option value="%" v-text="'%'" />
            <option value="rem" v-text="'rem'" />
            <option value="em" v-text="'em'" />
          </cvt-select>
        </div>
      </div>
    </div>

    <!-- Align Self Control -->
    <div class="mb-3">
      <cvt-select
        mode="dropdown"
        :value="alignSelf"
        :label="'Align Self'"
        :background-mode="mode"
        @input="(value) => updateAlignSelf(value)"
      >
        <option value="auto" v-text="'Auto'" />
        <option value="flex-start" v-text="'Flex Start'" />
        <option value="flex-end" v-text="'Flex End'" />
        <option value="center" v-text="'Center'" />
        <option value="stretch" v-text="'Stretch'" />
        <option value="baseline" v-text="'Baseline'" />
      </cvt-select>
    </div>

    <!-- Size Controls -->
    <div class="mb-3">
      <label class="form-label">Size</label>
      <cvt-radio-group
        orientation="horizontal"
        :initial-value="sizeType"
        class="row"
      >
        <cvt-radio-button size="md" value="grow" @radioClick="updateSizeType">
          Grow
        </cvt-radio-button>
        <cvt-radio-button size="md" value="shrink" @radioClick="updateSizeType">
          Shrink
        </cvt-radio-button>
        <cvt-radio-button size="md" value="custom" @radioClick="updateSizeType">
          Custom
        </cvt-radio-button>
      </cvt-radio-group>

      <!-- Custom Size Inputs (shown when custom is selected) -->
      <div v-if="sizeType === 'custom'" class="custom-size-inputs mt-3">
        <div class="row">
          <div class="col-6">
            <cvt-input
              type="number"
              :value="flexGrow"
              :label="'Flex Grow'"
              :placeholder="'Enter flex grow value'"
              @input="(value) => updateCustomFlexGrow(value)"
            />
          </div>
          <div class="col-6">
            <cvt-input
              type="number"
              :value="flexShrink"
              :label="'Flex Shrink'"
              :placeholder="'Enter flex shrink value'"
              @input="(value) => updateCustomFlexShrink(value)"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import * as getters from '@/js/store/modules/getters'
import * as _ from 'lodash'

export const useShowContainerItem = (selected) => {
  function getParent() {
    if (
      selected &&
      selected.parent() &&
      selected.parent()?.name === 'CContainerItem'
    ) {
      return selected.parent()
    }
    return null
  }
  return {
    isWrappedByCContainerItem: getParent() !== null,
  }
}

export default {
  name: 'CContainerItemOptions',
  components: {},
  props: {
    mode: {
      type: String,
      default: 'LIGHT',
    },
  },
  data() {
    return {
      allowHeightConfig: ['c-image'],
    }
  },
  computed: {
    ...mapGetters({
      config: getters.AUTH_GET_USER_CONFIG,
      selected: getters.SELECTOR_SELECTED,
      viewMode: getters.VIEW_MODE,
    }),
    selectedWrapper() {
      return this.selected.parent()
    },
    isSpecialStyle() {
      return this.config.SIDEBAR_CONFIGURATION
    },
    // Width properties
    widthType() {
      const width = this.selectedWrapper?.[this.viewMode]?.width || 'default'
      if (
        width === 'full-width' ||
        width === 'inline-auto' ||
        width === 'default'
      ) {
        return width
      }
      return 'custom'
    },
    customWidthValue() {
      const width = this.selectedWrapper?.[this.viewMode]?.width || 'full-width'
      if (width === 'full-width' || width === 'inline-auto') {
        return ''
      }
      // Extract numeric value from width string (e.g., "50px" -> "50")
      const match = width.match(/^(\d+(?:\.\d+)?)/)
      return match ? match[1] : ''
    },
    customWidthUnit() {
      const width = this.selectedWrapper?.[this.viewMode]?.width || 'full-width'
      if (width === 'full-width' || width === 'inline-auto') {
        return 'px'
      }
      // Extract unit from width string (e.g., "50px" -> "px")
      const match = width.match(/(px|%|rem|em)$/)
      return match ? match[1] : 'px'
    },
    canConfigureHeight() {
      return this.allowHeightConfig.includes(this.selected?.fbNode?.tag)
    },
    // Height properties
    heightType() {
      const height = this.selectedWrapper?.[this.viewMode]?.height || 'auto'
      if (height === 'full-height' || height === 'auto') {
        return height
      }
      return 'custom'
    },
    customHeightValue() {
      const height =
        this.selectedWrapper?.[this.viewMode]?.height || 'full-height'
      if (height === 'full-height' || height === 'auto') {
        return ''
      }
      // Extract numeric value from height string (e.g., "50px" -> "50")
      const match = height.match(/^(\d+(?:\.\d+)?)/)
      return match ? match[1] : ''
    },
    customHeightUnit() {
      const height =
        this.selectedWrapper?.[this.viewMode]?.height || 'full-height'
      if (height === 'full-height' || height === 'auto') {
        return 'px'
      }
      // Extract unit from height string (e.g., "50px" -> "px")
      const match = height.match(/(px|%|rem|em)$/)
      return match ? match[1] : 'px'
    },
    // Align self property
    alignSelf() {
      return this.selectedWrapper?.[this.viewMode]?.alignSelf || 'auto'
    },
    // Flex properties
    flexGrow() {
      return this.selectedWrapper?.[this.viewMode]?.flexGrow || 0
    },
    flexShrink() {
      return this.selectedWrapper?.[this.viewMode]?.flexShrink || 0
    },
    // Size type computed property
    sizeType() {
      if (this.selectedWrapper?.[this.viewMode]?.sizeType === 'custom') {
        return this.selectedWrapper?.[this.viewMode]?.sizeType
      }

      if (this.flexGrow > 0 && this.flexShrink === 0) {
        return 'grow'
      }

      if (this.flexGrow === 0 && this.flexShrink > 0) {
        return 'shrink'
      }

      if (this.selectedWrapper?.[this.viewMode]?.sizeType) {
        return this.selectedWrapper?.[this.viewMode]?.sizeType
      }

      return 'none'
    },
    // Custom flex values for custom mode
    customFlexGrow() {
      return this.selectedWrapper?.[this.viewMode]?.flexGrow || 0
    },
    customFlexShrink() {
      return this.selectedWrapper?.[this.viewMode]?.flexShrink || 1
    },
  },
  mounted() {},
  methods: {
    updateProp(value) {
      const parent = this.getParent()
      if (parent) {
        parent.updateProp(value.key, value.value)
      }
    },
    getParent() {
      if (
        this.selected &&
        this.selected.parent() &&
        this.selected.parent()?.name === 'CContainerItem'
      ) {
        return this.selected.parent()
      }
      return null
    },
    // Width methods
    updateWidthType(value) {
      if (value === 'custom') {
        // Keep current custom value if switching to custom
        this.updateProp({ key: `${this.viewMode}/width`, value: '100px' })
        return
      }
      this.updateProp({ key: `${this.viewMode}/width`, value })
    },
    updateCustomWidthValue(event) {
      const unit = this.customWidthUnit
      const newWidth = event.target.value
        ? `${event.target.value}${unit}`
        : `100${unit}`
      this.updateProp({ key: `${this.viewMode}/width`, value: newWidth })
    },
    updateCustomWidthUnit(value) {
      const currentValue = this.customWidthValue
      if (currentValue) {
        const newWidth = `${currentValue}${value}`
        this.updateProp({ key: `${this.viewMode}/width`, value: newWidth })
      }
    },
    // Height methods
    updateHeightType(value) {
      if (value === 'custom') {
        // Keep current custom value if switching to custom
        this.updateProp({ key: `${this.viewMode}/height`, value: '100px' })
        return
      }
      this.updateProp({ key: `${this.viewMode}/height`, value })
    },
    updateCustomHeightValue(event) {
      const unit = this.customHeightUnit
      const newHeight = event.target.value
        ? `${event.target.value}${unit}`
        : `100${unit}`
      this.updateProp({ key: `${this.viewMode}/height`, value: newHeight })
    },
    updateCustomHeightUnit(value) {
      const currentValue = this.customHeightValue
      if (currentValue) {
        const newHeight = `${currentValue}${value}`
        this.updateProp({ key: `${this.viewMode}/height`, value: newHeight })
      }
    },
    // Align self method
    updateAlignSelf(value) {
      this.updateProp({ key: `${this.viewMode}/alignSelf`, value })
    },
    // Flex methods
    updateFlexGrow(value) {
      this.updateProp({
        key: `${this.viewMode}/flexGrow`,
        value: parseInt(value),
      })
    },
    updateFlexShrink(value) {
      this.updateProp({
        key: `${this.viewMode}/flexShrink`,
        value: parseInt(value),
      })
    },
    updateFlexBasis(value) {
      this.updateProp({ key: `${this.viewMode}/flexBasis`, value })
    },
    // Size methods
    updateSizeType(value) {
      let grow, shrink

      if (value === this.sizeType) {
        // If the same value is selected, do nothing
        value = 'none'
      }

      switch (value) {
        case 'none':
          grow = 0
          shrink = 0
          break
        case 'grow':
          grow = 1
          shrink = 0
          break
        case 'shrink':
          grow = 0
          shrink = 1
          break
        case 'custom':
          // Keep current values when switching to custom
          grow = 0
          shrink = 0
          break
        default:
          return
      }

      // Update both properties
      this.updateProp({ key: `${this.viewMode}/sizeType`, value })
      this.updateProp({ key: `${this.viewMode}/flexGrow`, value: grow })
      this.updateProp({ key: `${this.viewMode}/flexShrink`, value: shrink })
    },
    updateCustomFlexGrow(event) {
      this.updateProp({
        key: `${this.viewMode}/flexGrow`,
        value: parseInt(event.target.value),
      })
    },
    updateCustomFlexShrink(event) {
      this.updateProp({
        key: `${this.viewMode}/flexShrink`,
        value: parseInt(event.target.value),
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.c-container-item-options {
  padding: 1rem;
  padding-top: 0;

  .form-group {
    margin-bottom: 1rem;
  }

  .form-label {
    font-weight: 500;
    margin-bottom: 0.5rem;
    display: block;
  }

  .custom-size-inputs {
    border-top: 1px solid #e9ecef;
    padding-top: 1rem;
  }

  .row {
    margin-left: -0.5rem;
    margin-right: -0.5rem;

    .col-4,
    .col-6,
    .col-8 {
      padding-left: 0.5rem;
      padding-right: 0.5rem;
    }
  }
}
</style>
